import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Candidate, CandidateRating, AssessmentPoint } from '../../types';
import Button from '../Form/Button';
import CandidateRatingForm from './CandidateRatingForm';
import { useAssessmentPoints } from '../../hooks/useAssessmentPoints';
import { useEmployees } from '../../hooks/useEmployees';
import {
  CandidatePersonalInfo,
  CandidateApplicationDetails,
  CandidateProfessionalBackground,
  CandidateManagementSection,
} from './CandidateFormSections';

interface CandidateFormProps {
  candidate?: Candidate;
  onSubmit: (data: Partial<Candidate>) => void;
  onCancel: () => void;
}

interface CandidateFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  nationality: string;
  dateOfBirth: string;
  appliedPosition: string;
  source: 'website' | 'linkedin' | 'referral' | 'job_board' | 'other';
  experience: number;
  education: string;
  certificates: string[];
  languages: string[];
  previousCompany: string;
  expectedSalary: number;
  availableFrom: string;
  coverLetter: string;
  status: 'new' | 'under_review' | 'interview_scheduled' | 'interviewed' | 'hired' | 'rejected';
  assignedManagerId: string;
  assignedManagerName: string;
  managerFeedback: string;
  interviewDate?: string;
  notes: string;
}

const CandidateForm: React.FC<CandidateFormProps> = ({ candidate, onSubmit, onCancel }) => {
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [candidateRatings, setCandidateRatings] = useState<CandidateRating[]>(candidate?.rating || []);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvFileName, setCvFileName] = useState<string>('');
  
  const queryOptions = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: apiAssessmentPoints } = useAssessmentPoints(queryOptions);
  const { data: employeesData } = useEmployees(queryOptions);
  const assessmentPoints: AssessmentPoint[] = useMemo(() => {
    if (apiAssessmentPoints && Array.isArray(apiAssessmentPoints) && apiAssessmentPoints.length > 0) {
      return apiAssessmentPoints.filter((p: any) => p.isActive !== false);
    }
    return [];
  }, [apiAssessmentPoints]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue
  } = useForm<CandidateFormData>({
    defaultValues: candidate ? {
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      email: candidate.email,
      phone: candidate.phone,
      nationality: candidate.nationality,
      dateOfBirth: candidate.dateOfBirth,
      appliedPosition: candidate.appliedPosition,
      source: candidate.source,
      experience: candidate.experience,
      education: candidate.education,
      certificates: candidate.certificates,
      languages: candidate.languages,
      previousCompany: candidate.previousCompany,
      expectedSalary: candidate.expectedSalary,
      availableFrom: candidate.availableFrom,
      coverLetter: candidate.coverLetter,
      status: candidate.status,
      assignedManagerId: candidate.assignedManagerId,
      assignedManagerName: candidate.assignedManagerName,
      managerFeedback: candidate.managerFeedback,
      interviewDate: candidate.interviewDate ? candidate.interviewDate.split('T')[0] : '',
      notes: candidate.notes
    } : {
      firstName: '', lastName: '', email: '', phone: '', nationality: '',
      dateOfBirth: '', appliedPosition: '', source: 'website', experience: 0,
      education: '', certificates: [], languages: [], previousCompany: '',
      expectedSalary: 0, availableFrom: '', coverLetter: '', status: 'new',
      assignedManagerId: '', assignedManagerName: '', managerFeedback: '',
      interviewDate: '', notes: ''
    }
  });

  const sourceOptions = [
    { value: 'website', label: 'Company Website' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'referral', label: 'Employee Referral' },
    { value: 'job_board', label: 'Job Board' },
    { value: 'other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'new', label: 'New Application' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'interview_scheduled', label: 'Interview Scheduled' },
    { value: 'interviewed', label: 'Interviewed' },
    { value: 'hired', label: 'Hired' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const managerOptions = useMemo(() => {
    if (!employeesData || !Array.isArray(employeesData)) return [];
    return employeesData.map((emp: any) => ({
      value: emp.documentId || emp.id || '',
      label: `${emp.firstName || ''} ${emp.lastName || ''} ${emp.position ? '- ' + emp.position : ''}`.trim()
    }));
  }, [employeesData]);

  const [certificates, setCertificates] = React.useState<string>(
    candidate?.certificates?.join(', ') || ''
  );
  const [languages, setLanguages] = React.useState<string>(
    candidate?.languages?.join(', ') || ''
  );

  const handleCertificatesChange = (value: string) => {
    setCertificates(value);
    const certArray = value.split(',').map(cert => cert.trim()).filter(cert => cert.length > 0);
    setValue('certificates', certArray);
  };

  const handleLanguagesChange = (value: string) => {
    setLanguages(value);
    const langArray = value.split(',').map(lang => lang.trim()).filter(lang => lang.length > 0);
    setValue('languages', langArray);
  };

  const handleManagerChange = (managerId: string) => {
    setValue('assignedManagerId', managerId);
    const manager = managerOptions.find((m: any) => m.value === managerId);
    if (manager) {
      setValue('assignedManagerName', manager.label.split(' - ')[0].trim());
    }
  };

  const calculateTotalRating = (ratings: CandidateRating[]) => {
    if (ratings.length === 0) return 0;
    let totalWeightedScore = 0;
    let totalWeight = 0;
    ratings.forEach(rating => {
      const point = assessmentPoints.find(p => (p.id === rating.pointId || p.documentId === rating.pointId));
      if (point) {
        totalWeightedScore += rating.score * point.weight;
        totalWeight += point.weight;
      }
    });
    return totalWeight > 0 ? Math.round((totalWeightedScore / totalWeight) * 10) / 10 : 0;
  };

  const handleRatingSave = (newRatings: CandidateRating[]) => {
    setCandidateRatings(newRatings);
    setShowRatingForm(false);
  };

  const handleCvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCvFile(file);
      setCvFileName(file.name);
    }
  };

  const handleFormSubmit = (data: CandidateFormData) => {
    const totalRating = calculateTotalRating(candidateRatings);
    const submitData = {
      ...data,
      experience: Number(data.experience),
      expectedSalary: Number(data.expectedSalary),
      rating: candidateRatings,
      totalRating,
      interviewDate: data.interviewDate ? new Date(data.interviewDate).toISOString() : undefined,
      appliedDate: candidate?.appliedDate || new Date().toISOString().split('T')[0],
      cvFile: cvFile || undefined
    };
    onSubmit(submitData);
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="mb-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {candidate ? 'Edit Candidate' : 'Add New Candidate'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {candidate ? 'Update the candidate information below.' : 'Fill in the candidate details from their application.'}
          </p>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <CandidatePersonalInfo register={register as any} errors={errors as any} />

          <CandidateApplicationDetails
            register={register as any}
            errors={errors as any}
            sourceOptions={sourceOptions}
            cvFileName={cvFileName}
            onCvFileChange={handleCvFileChange}
            onCvFileClear={() => { setCvFile(null); setCvFileName(''); }}
            isEditing={!!candidate}
          />

          <CandidateProfessionalBackground
            register={register as any}
            errors={errors as any}
            certificates={certificates}
            languages={languages}
            onCertificatesChange={handleCertificatesChange}
            onLanguagesChange={handleLanguagesChange}
          />

          <CandidateManagementSection
            register={register as any}
            errors={errors as any}
            statusOptions={statusOptions}
            managerOptions={managerOptions}
            candidateRatings={candidateRatings}
            totalRating={calculateTotalRating(candidateRatings)}
            onManagerChange={handleManagerChange}
            onShowRatingForm={() => setShowRatingForm(true)}
          />

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : candidate ? 'Update Candidate' : 'Add Candidate'}
            </Button>
          </div>
        </form>
      </div>

      {showRatingForm && (
        <CandidateRatingForm
          existingRatings={candidateRatings}
          assessmentPoints={assessmentPoints}
          onSave={handleRatingSave}
          onCancel={() => setShowRatingForm(false)}
          evaluatorName="Current User"
        />
      )}
    </div>
  );
};

export default CandidateForm;
